import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Header } from '@/components/Header';
import { SearchBar } from '@/components/SearchBar';
import { PhraseCard } from '@/components/PhraseCard';
import { LoadingDino } from '@/components/LoadingDino';
import { DinoLlamaLoader } from '@/components/DinoLlamaLoader';
import { PhraseOfTheDay } from '@/components/PhraseOfTheDay';
import { MostLovedPhraseCard } from '@/components/MostLovedPhraseCard';
import QuickStats from '@/components/QuickStats';
import { FriendsActivityFeed } from '@/components/FriendsActivityFeed';
import { shouldHideOtherPlatforms } from '@/lib/platform';
import { RegionalDialectSelector } from '@/components/RegionalDialectSelector';
import { ErrorPageContent } from '@/components/DetectiveDino';
import { BottomNav } from '@/components/BottomNav';
import { LinktreeHub } from '@/components/LinktreeHub';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { usePhrases } from '@/context/PhrasesContext';
import { useLanguage } from '@/context/LanguageContext';
import { toast } from 'sonner';
import { 
  BookOpen, Search as SearchIcon, Sparkles, MessageSquare, ArrowLeft,
  Calendar, Globe, Map as MapIcon, MapPin, Users, Baby, Zap, Coffee, Radio, Tv,
  Umbrella, Sun, Mountain, Star, Wheat, Building, Sunset, Leaf, Music,
  Briefcase, Heart, Home, Book, Smile, Smartphone, ShoppingBag, HeartPulse,
  PalmtreeIcon, Anchor, Castle, Trees, Languages, Flag, ChevronRight
} from 'lucide-react';
import { Seo } from '@/components/Seo';

// Icon mapping for categories
const iconMap = {
  'calendar': Calendar,
  'globe': Globe,
  'map': MapIcon,
  'map-pin': MapPin,
  'users': Users,
  'baby': Baby,
  'zap': Zap,
  'coffee': Coffee,
  'radio': Radio,
  'tv': Tv,
  'umbrella': Umbrella,
  'sun': Sun,
  'mountain': Mountain,
  'star': Star,
  'wheat': Wheat,
  'building': Building,
  'sunset': Sunset,
  'leaf': Leaf,
  'music': Music,
  'briefcase': Briefcase,
  'heart': Heart,
  'home': Home,
  'book': Book,
  'smile': Smile,
  'smartphone': Smartphone,
  'shopping-bag': ShoppingBag,
  'heart-pulse': HeartPulse,
  'palmtree': PalmtreeIcon,
  'anchor': Anchor,
  'castle': Castle,
  'trees': Trees,
  'languages': Languages,
  'flag': Flag,
  'message-square': MessageSquare,
  'cactus': Sun, // fallback
};

// Color classes mapping for vibrant colors (Tailwind needs static classes)
const colorClasses = {
  'primary': { bg: 'bg-primary/10', text: 'text-primary' },
  'rose': { bg: 'bg-rose-500/10', text: 'text-rose-500' },
  'emerald': { bg: 'bg-emerald-500/10', text: 'text-emerald-500' },
  'cyan': { bg: 'bg-cyan-500/10', text: 'text-cyan-500' },
  'blue': { bg: 'bg-blue-500/10', text: 'text-blue-500' },
  'violet': { bg: 'bg-violet-500/10', text: 'text-violet-500' },
  'orange': { bg: 'bg-orange-500/10', text: 'text-orange-500' },
  'red': { bg: 'bg-red-500/10', text: 'text-red-500' },
  'pink': { bg: 'bg-pink-500/10', text: 'text-pink-500' },
  'teal': { bg: 'bg-teal-500/10', text: 'text-teal-500' },
  'lime': { bg: 'bg-lime-500/10', text: 'text-lime-500' },
  'green': { bg: 'bg-green-500/10', text: 'text-green-500' },
  'indigo': { bg: 'bg-indigo-500/10', text: 'text-indigo-500' },
  'purple': { bg: 'bg-purple-500/10', text: 'text-purple-500' },
  'sky': { bg: 'bg-sky-500/10', text: 'text-sky-500' },
  'fuchsia': { bg: 'bg-fuchsia-500/10', text: 'text-fuchsia-500' },
};

const getColorClasses = (color) => {
  return colorClasses[color] || colorClasses['primary'];
};

const HomePage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { categories, phrases, isLoading, error, searchPhrases: contextSearchPhrases, getPhrasesByCategory: contextGetPhrasesByCategory } = usePhrases();
  const { t, isSpanish, translateCategories, isTranslating } = useLanguage();
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredPhrases, setFilteredPhrases] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [translatedCats, setTranslatedCats] = useState([]);
  
  // Get category state from URL params (persists across navigation)
  const selectedMainCategory = searchParams.get('category') || null;
  const selectedSubCategory = searchParams.get('subcategory') || null;
  const selectedDialect = searchParams.get('dialect') || null;
  
  // Dialect name mapping for display
  const dialectNames = {
    'british': 'British', 'irish': 'Irish', 'aussie': 'Australian', 'newzealand': 'New Zealand',
    'canadian': 'Canadian', 'caribbean': 'Caribbean', 'southafrican': 'South African',
    'african': 'African', 'asian': 'Asian', 'eastcoast': 'US East Coast',
    'westcoast': 'US West Coast', 'midwest': 'US Midwest', 'southern': 'US Southern',
    'deepsouth': 'US Deep South', 'texas': 'Texas',
    'es-mexico': 'México', 'es-spain': 'España', 'es-southamerica': 'Sudamérica',
    'es-centralamerica': 'Centroamérica', 'es-puertorico': 'Puerto Rico',
    'es-texas': 'Texas (Español)', 'es-california': 'California',
    'es-newmexico': 'Nuevo México', 'es-canary-islands': 'Islas Canarias',
    'es-easter-island': 'Isla de Pascua', 'es-guinea': 'Guinea Ecuatorial',
    'es-chavacano': 'Chavacano', 'es-islands': 'Caribbean Spanish',
  };
  
  // Translate categories when language changes
  useEffect(() => {
    const doTranslate = async () => {
      if (categories.length > 0) {
        const translated = await translateCategories(categories);
        setTranslatedCats(translated);
      }
    };
    doTranslate();
  }, [categories, translateCategories, isSpanish]);
  
  // Use translated categories if available, otherwise use original
  const displayCategories = translatedCats.length > 0 ? translatedCats : categories;
  
  // Separate main categories and subcategories - sorted alphabetically
  const mainCategories = displayCategories.filter(c => c.isMainCategory).sort((a, b) => a.name.localeCompare(b.name));
  const getSubcategories = (mainCatId) => {
    const mainCat = mainCategories.find(c => c.id === mainCatId);
    if (!mainCat || !mainCat.subcategories) return [];
    return displayCategories.filter(c => mainCat.subcategories.includes(c.id)).sort((a, b) => a.name.localeCompare(b.name));
  };
  
  // Handle URL search, dialect, and category parameters - restore state on navigation
  useEffect(() => {
    const urlSearch = searchParams.get('search');
    const urlDialect = searchParams.get('dialect');
    const urlCategory = searchParams.get('category');
    const urlSubcategory = searchParams.get('subcategory');
    
    if (phrases.length > 0) {
      if (urlDialect) {
        // Restore dialect view
        const filtered = phrases.filter(p => p.category === urlDialect).sort((a, b) => a.phrase.localeCompare(b.phrase));
        setFilteredPhrases(filtered);
      } else if (urlSubcategory) {
        // Restore subcategory view
        const filtered = contextGetPhrasesByCategory(urlSubcategory);
        setFilteredPhrases(filtered.sort((a, b) => a.phrase.localeCompare(b.phrase)));
      } else if (urlCategory) {
        // Category selected but no subcategory - show subcategories (no phrases)
        setFilteredPhrases([]);
      } else if (urlSearch) {
        // Restore search results
        setSearchQuery(urlSearch);
        setIsSearching(true);
        const results = contextSearchPhrases(urlSearch);
        const consolidated = consolidateResults(results);
        setFilteredPhrases(consolidated);
        if (consolidated.length > 0) {
          toast.success(`Found ${consolidated.length} ${consolidated.length === 1 ? 'phrase' : 'phrases'} for "${urlSearch}"`);
        }
      } else {
        // Home view - no filters
        setFilteredPhrases([]);
      }
    }
  }, [searchParams, phrases, contextSearchPhrases, contextGetPhrasesByCategory]);
  
  // Update filtered phrases when phrases data loads - DON'T auto-select category
  // But DO re-filter if a subcategory or dialect is selected via URL
  useEffect(() => {
    if (phrases.length > 0) {
      const urlSubcategory = searchParams.get('subcategory');
      const urlDialect = searchParams.get('dialect');
      const urlSearch = searchParams.get('search');
      
      if (urlDialect) {
        const filtered = phrases.filter(p => p.category === urlDialect).sort((a, b) => a.phrase.localeCompare(b.phrase));
        setFilteredPhrases(filtered);
      } else if (urlSubcategory) {
        const filtered = phrases.filter(p => p.category === urlSubcategory).sort((a, b) => a.phrase.localeCompare(b.phrase));
        setFilteredPhrases(filtered);
      } else if (!urlSearch) {
        setFilteredPhrases([]);
      }
    }
  }, [phrases, searchParams]);
  
  // Handle dialect selection - update URL instead of local state
  const handleDialectSelect = (dialectId) => {
    setSearchQuery('');
    setIsSearching(false);
    setSearchParams({ dialect: dialectId });
    
    const filtered = phrases.filter(p => p.category === dialectId).sort((a, b) => a.phrase.localeCompare(b.phrase));
    setFilteredPhrases(filtered);
    
    const dialectName = dialectNames[dialectId] || dialectId;
    toast.success(`${isSpanish ? 'Mostrando' : 'Showing'} ${filtered.length} ${isSpanish ? 'frases de' : 'phrases from'} ${dialectName}`);
  };
  
  const handleBackFromDialect = () => {
    setFilteredPhrases([]);
    setSearchParams({});
  };
  
  // Consolidate search results — group same phrase across categories into one card
  const consolidateResults = (results) => {
    const consolidatedMap = new Map();
    
    results.forEach(phrase => {
      const key = phrase.phrase.toLowerCase().trim();
      if (consolidatedMap.has(key)) {
        const existing = consolidatedMap.get(key);
        if (!existing.categories) {
          existing.categories = [existing.category];
          existing.allMeanings = [existing.actualMeaning];
        }
        if (!existing.categories.includes(phrase.category)) {
          existing.categories.push(phrase.category);
        }
        if (!existing.allMeanings.includes(phrase.actualMeaning)) {
          existing.allMeanings.push(phrase.actualMeaning);
        }
        if (!existing.allIds) {
          existing.allIds = [existing.id];
        }
        existing.allIds.push(phrase.id);
      } else {
        consolidatedMap.set(key, {
          ...phrase,
          categories: [phrase.category],
          allMeanings: [phrase.actualMeaning],
          allIds: [phrase.id]
        });
      }
    });
    
    return Array.from(consolidatedMap.values())
      .sort((a, b) => a.phrase.localeCompare(b.phrase));
  };

  const handleSearch = (query) => {
    const trimmedQuery = query?.trim() || '';
    setSearchQuery(trimmedQuery);
    setIsSearching(!!trimmedQuery);
    
    if (!trimmedQuery) {
      setFilteredPhrases([]);
      setSearchParams({});
      return;
    }
    
    // Clear category params when searching
    setSearchParams({ search: trimmedQuery });
    
    const results = contextSearchPhrases(trimmedQuery);
    const consolidatedResults = consolidateResults(results);
    
    setFilteredPhrases(consolidatedResults);
    
    if (consolidatedResults.length === 0) {
      toast.info('No phrases found', {
        description: 'Try different keywords or browse categories below'
      });
    } else {
      toast.success(`Found ${consolidatedResults.length} ${consolidatedResults.length === 1 ? 'phrase' : 'phrases'}`);
    }
  };
  
  const handleMainCategoryClick = (categoryId) => {
    setSearchQuery('');
    setIsSearching(false);
    setFilteredPhrases([]);
    setSearchParams({ category: categoryId });
  };
  
  const handleSubCategoryClick = (categoryId) => {
    const filtered = contextGetPhrasesByCategory(categoryId);
    setFilteredPhrases(filtered.sort((a, b) => a.phrase.localeCompare(b.phrase)));
    setSearchParams({ category: selectedMainCategory, subcategory: categoryId });
  };
  
  const handleBackToMain = () => {
    setFilteredPhrases([]);
    setSearchParams({});
  };
  
  const handleBackToSubcategories = () => {
    setFilteredPhrases([]);
    setSearchParams({ category: selectedMainCategory });
  };
  
  const handlePhraseClick = (phraseId) => {
    navigate(`/phrase/${phraseId}`);
  };
  
  const getIcon = (iconName) => {
    const IconComponent = iconMap[iconName] || MessageSquare;
    return IconComponent;
  };
  
  // Show loading dinosaur while data loads
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <LoadingDino message={isSpanish ? 'Cargando 3,400+ frases...' : 'Loading 3,400+ phrases from database...'} />
        <BottomNav />
      </div>
    );
  }
  
  // Show error state with retry
  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="container py-12 text-center">
          <ErrorPageContent 
            isSpanish={isSpanish}
            onRetry={() => window.location.reload()}
          />
        </div>
      </div>
    );
  }
  
  // Current subcategories if a main category is selected
  const currentSubcategories = selectedMainCategory ? getSubcategories(selectedMainCategory) : [];
  const currentMainCategory = mainCategories.find(c => c.id === selectedMainCategory);
  const currentSubCategory = displayCategories.find(c => c.id === selectedSubCategory);
  
  return (
    <div className="min-h-dvh bg-gradient-to-br from-primary/5 via-secondary/5 to-accent/5 pb-20">
      <Seo
        path="/library"
        title="Phrase Library — ClearCommunicationApp"
        description="Browse 3,000+ neurodivergent-friendly phrases across categories like idioms, sarcasm, medical, business, and more — in English and Spanish, with audio."
      />
      <Header />
      
      {/* Hero Section - only show on home view */}
      {!isSearching && !selectedMainCategory && !selectedDialect && (
        <section className="relative py-3 sm:py-8">
          <div className="container">
            <div className="max-w-4xl mx-auto text-center space-y-2 sm:space-y-5">
              <div className="hidden sm:inline-flex items-center space-x-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium border border-primary/20">
                <Sparkles className="h-4 w-4" />
                <span style={{ 
                  WebkitTextStroke: '1px #9333ea',
                  paintOrder: 'stroke fill'
                }}>
                  {t('heroTagline')}
                </span>
              </div>
              
              <h1 className={`font-bold leading-tight tracking-tight ${isSpanish ? 'text-xl sm:text-4xl lg:text-5xl' : 'text-2xl sm:text-5xl lg:text-6xl'}`}>
                {t('heroTitle1')}
                <span className="block mt-1 sm:mt-2 bg-gradient-to-r from-[#00E5CC] to-purple-600 bg-clip-text text-transparent">
                  {t('heroTitle2')}
                </span>
              </h1>
              
              <p className="hidden sm:block text-lg sm:text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
                {t('heroDescription')}
              </p>
              
              <div className="max-w-2xl mx-auto">
                <p className="hidden sm:block font-bold text-foreground mb-3">
                  {t('heroSearchPrompt')}
                </p>
                <SearchBar onSearch={handleSearch} placeholder={t('searchPlaceholder')} />
              </div>
            </div>
          </div>
        </section>
      )}
      
      {/* App Store Banner - only show on home view AND non-iOS clients
          (Apple guideline 2.3.3 forbids cross-promoting non-Apple stores on iOS). */}
      {!isSearching && !selectedMainCategory && !selectedDialect && !shouldHideOtherPlatforms() && (
        <div className="px-4 text-center mt-2 mb-2">
          <div className="inline-flex flex-wrap items-center justify-center gap-2">
            <a
              href="https://play.google.com/store/apps/details?id=www.ClearCommunicationApp.com"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#01875F] hover:bg-[#016F4F] text-white font-semibold text-sm transition-colors shadow-md"
              data-testid="google-play-hero"
            >
              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M3.609 1.814 13.792 12 3.61 22.186a.996.996 0 0 1-.61-.92V2.734a1 1 0 0 1 .609-.92zm10.89 10.893 2.302 2.302-10.937 6.333 8.635-8.635zM21.61 11.13a1 1 0 0 1 0 1.74l-2.703 1.566-2.508-2.508 2.508-2.508 2.703 1.566zM5.864 2.485l10.937 6.333-2.302 2.302-8.635-8.635z"/>
              </svg>
              {isSpanish ? 'Disponible en Google Play' : 'Available on Google Play'}
            </a>
            <a
              href="https://www.amazon.com/gp/product/B0GX2ZJQGV"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#FF9900] hover:bg-[#e88b00] text-white font-semibold text-sm transition-colors shadow-md"
              data-testid="amazon-appstore-hero"
            >
              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor"><path d="M13.958 10.09c0 1.232.029 2.256-.591 3.351-.502.891-1.301 1.438-2.186 1.438-1.214 0-1.922-.924-1.922-2.292 0-2.692 2.415-3.182 4.7-3.182v.685zm3.186 7.705a.66.66 0 01-.753.077c-1.058-.878-1.248-1.286-1.828-2.122-1.748 1.783-2.986 2.316-5.251 2.316-2.681 0-4.764-1.655-4.764-4.966 0-2.585 1.401-4.344 3.394-5.205 1.726-.753 4.137-.887 5.982-1.095v-.41c0-.753.058-1.642-.384-2.292-.384-.579-1.118-.819-1.766-.819-1.199 0-2.266.615-2.527 1.89a.532.532 0 01-.461.461l-2.585-.279a.454.454 0 01-.384-.536C6.462 1.97 9.293.779 11.869.779c1.327 0 3.062.353 4.108 1.357 1.326 1.247 1.199 2.911 1.199 4.725v4.276c0 1.285.533 1.849 1.034 2.543.178.245.213.544-.013.721-.564.472-1.569 1.348-2.12 1.841l-.003-.003-.93.557z"/><path d="M21.625 19.516c-2.904 2.139-7.122 3.28-10.751 3.28-5.082 0-9.66-1.879-13.121-5.006-.272-.245-.03-.579.298-.39 3.735 2.173 8.35 3.483 13.121 3.483 3.218 0 6.755-.666 10.01-2.047.49-.214.901.322.443.68z"/></svg>
              {isSpanish ? 'Amazon App Store' : 'Amazon App Store'}
            </a>
          </div>
          <p className="text-xs text-muted-foreground leading-tight mt-1">
            ({isSpanish ? 'También disponible en la web' : 'Also available on the web'})
          </p>
        </div>
      )}
      
      {/* Phrase of the Day */}
      {!isSearching && !selectedMainCategory && !selectedDialect && (
        <PhraseOfTheDay />
      )}

      {/* Most-loved phrase of the week — companion widget to the blog
          version on /blog. Auto-hides when no phrase has been saved
          in the last 7 days. */}
      {!isSearching && !selectedMainCategory && !selectedDialect && (
        <MostLovedPhraseCard isSpanish={isSpanish} />
      )}
      
      {/* Quick Stats Widget */}
      {!isSearching && !selectedMainCategory && !selectedDialect && (
        <QuickStats />
      )}

      {/* Friends activity feed (only renders if user has friends) */}
      {!isSearching && !selectedMainCategory && !selectedDialect && (
        <div className="container max-w-2xl mx-auto px-4 my-4">
          <FriendsActivityFeed limit={6} />
        </div>
      )}
      
      {/* Regional Dialect Selector */}
      {!isSearching && !selectedMainCategory && !selectedDialect && (
        <section className="py-3 sm:py-5">
          <div className="container">
            <RegionalDialectSelector onSelectDialect={handleDialectSelect} />
          </div>
        </section>
      )}
      
      {/* Main Content */}
      <section className="py-4 sm:py-8">
        <div className="container">
          {/* Dialect View */}
          {selectedDialect && (
            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <Button size="sm" onClick={handleBackFromDialect} className="bg-[#00E5CC] hover:bg-[#00C9B3] text-[#0d1f2d] font-medium">
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  {isSpanish ? 'Volver' : 'Back'}
                </Button>
              </div>
              
              <div>
                <h2 className="text-2xl sm:text-3xl font-bold">{dialectNames[selectedDialect] || selectedDialect}</h2>
                <p className="text-muted-foreground mt-1">
                  {filteredPhrases.length} {filteredPhrases.length === 1 ? t('phrase') : t('phrases')} {isSpanish ? 'en este dialecto' : 'in this dialect'}
                </p>
              </div>
              
              {filteredPhrases.length > 0 ? (
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {filteredPhrases.map((phrase) => (
                    <PhraseCard
                      key={phrase.id}
                      phrase={phrase}
                      onClick={() => handlePhraseClick(phrase.id)}
                      translatedCategories={translatedCats}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 bg-muted/30 rounded-lg">
                  <Globe className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold mb-2">{t('noPhrases')}</h3>
                  <p className="text-muted-foreground mb-4">
                    {isSpanish ? 'Pronto agregaremos más frases para este dialecto' : 'More phrases coming soon for this dialect'}
                  </p>
                </div>
              )}
            </div>
          )}
          
          {/* Search Results */}
          {isSearching && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl sm:text-3xl font-bold">{t('searchResults')}</h2>
                  <p className="text-muted-foreground mt-1">
                    {t('found')} {filteredPhrases.length} {filteredPhrases.length === 1 ? t('phrase') : t('phrases')} {t('for')} "{searchQuery}"
                  </p>
                </div>
                <Button 
                  variant="outline" 
                  onClick={() => { setIsSearching(false); setSearchQuery(''); setFilteredPhrases([]); }}
                  className="border-2 border-gray-600 bg-gray-100 text-gray-800 hover:bg-gray-200 hover:border-gray-700 font-semibold shadow-sm"
                  data-testid="clear-search-btn"
                >
                  {t('clearSearch')}
                </Button>
              </div>
              
              {filteredPhrases.length > 0 ? (
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {filteredPhrases.map((phrase) => (
                    <PhraseCard
                      key={phrase.id}
                      phrase={phrase}
                      onClick={() => handlePhraseClick(phrase.id)}
                      translatedCategories={translatedCats}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 bg-muted/30 rounded-lg">
                  <SearchIcon className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold mb-2">{t('noPhrasesFound')}</h3>
                  <p className="text-muted-foreground mb-4">
                    {t('tryDifferent')}
                  </p>
                  <Button className="bg-[#00E5CC] hover:bg-[#00C9B3] text-[#0d1f2d] font-medium" onClick={() => navigate('/feedback')}>
                    {t('requestPhrase')}
                  </Button>
                </div>
              )}
            </div>
          )}
          
          {/* Main Categories View */}
          {!isSearching && !selectedMainCategory && !selectedDialect && (
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl sm:text-3xl font-bold">{t('browseByCategory')}</h2>
                <p className="text-muted-foreground mt-1">{t('selectCategory')}</p>
              </div>
              
              {isTranslating && (
                <DinoLlamaLoader message={t('translating')} size="sm" />
              )}
              
              <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
                {mainCategories.map((category) => {
                  const IconComponent = getIcon(category.icon);
                  return (
                    <Card 
                      key={category.id}
                      className="cursor-pointer hover:shadow-lg transition-all hover:scale-[1.02] border-2 hover:border-primary/50"
                      onClick={() => handleMainCategoryClick(category.id)}
                      data-testid="category-chip"
                    >
                      <CardHeader>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`p-3 rounded-full ${getColorClasses(category.color).bg}`}>
                              <IconComponent className={`h-6 w-6 ${getColorClasses(category.color).text}`} />
                            </div>
                            <div>
                              <CardTitle className="text-lg">{category.name}</CardTitle>
                              <CardDescription className="text-sm">{category.description}</CardDescription>
                            </div>
                          </div>
                          <ChevronRight className="h-5 w-5 text-muted-foreground" />
                        </div>
                      </CardHeader>
                    </Card>
                  );
                })}
              </div>
            </div>
          )}
          
          {/* Subcategories View */}
          {!isSearching && selectedMainCategory && !selectedSubCategory && (
            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <Button size="sm" onClick={handleBackToMain} className="bg-[#00E5CC] hover:bg-[#00C9B3] text-[#0d1f2d] font-medium">
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  {t('backToCategories')}
                </Button>
              </div>
              
              <div>
                <h2 className="text-2xl sm:text-3xl font-bold">{currentMainCategory?.name}</h2>
                <p className="text-muted-foreground mt-1">{t('selectSubcategory')}</p>
              </div>
              
              <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
                {currentSubcategories.map((category) => {
                  const IconComponent = getIcon(category.icon);
                  const phraseCount = phrases.filter(p => p.category === category.id).length;
                  const colors = getColorClasses(category.color);
                  return (
                    <Card 
                      key={category.id}
                      className="cursor-pointer hover:shadow-lg transition-all hover:scale-[1.02] border-2 hover:border-primary/50"
                      onClick={() => handleSubCategoryClick(category.id)}
                    >
                      <CardHeader className="pb-3">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex items-start gap-3">
                            <div className={`p-2.5 rounded-full ${colors.bg} flex-shrink-0 mt-0.5`}>
                              <IconComponent className={`h-5 w-5 ${colors.text}`} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <CardTitle className="text-base font-semibold leading-tight">{category.name}</CardTitle>
                              {phraseCount > 0 && (
                                <span className={`text-xs ${colors.text} font-medium`}>
                                  {phraseCount} {phraseCount === 1 ? t('phrase') : t('phrases')}
                                </span>
                              )}
                            </div>
                          </div>
                          <ChevronRight className="h-5 w-5 text-muted-foreground flex-shrink-0 mt-0.5" />
                        </div>
                        {category.description && (
                          <p className="text-xs text-muted-foreground mt-2 pl-10 leading-relaxed">
                            {category.description}
                          </p>
                        )}
                      </CardHeader>
                    </Card>
                  );
                })}
              </div>
            </div>
          )}
          
          {/* Phrases View (after selecting subcategory) */}
          {!isSearching && selectedSubCategory && (
            <div className="space-y-6">
              <div className="flex items-center gap-4 flex-wrap">
                <Button size="sm" onClick={handleBackToMain} className="bg-[#00E5CC] hover:bg-[#00C9B3] text-[#0d1f2d] font-medium">
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  {t('backToCategories')}
                </Button>
                <Button size="sm" onClick={handleBackToSubcategories} className="bg-[#00E5CC]/80 hover:bg-[#00C9B3] text-[#0d1f2d] font-medium">
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  {t('backTo')} {currentMainCategory?.name}
                </Button>
              </div>
              
              <div>
                <h2 className="text-2xl sm:text-3xl font-bold">{currentSubCategory?.name}</h2>
                {currentSubCategory?.description && (
                  <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
                    {currentSubCategory.description}
                  </p>
                )}
                <p className="text-muted-foreground mt-2">
                  {filteredPhrases.length} {filteredPhrases.length === 1 ? t('phrase') : t('phrases')} {t('inThisCategory')}
                </p>
              </div>
              
              {filteredPhrases.length > 0 ? (
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {filteredPhrases.map((phrase) => (
                    <PhraseCard
                      key={phrase.id}
                      phrase={phrase}
                      onClick={() => handlePhraseClick(phrase.id)}
                      translatedCategories={translatedCats}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 bg-muted/30 rounded-lg">
                  <BookOpen className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold mb-2">{t('noPhrases')}</h3>
                  <p className="text-muted-foreground mb-4">
                    {t('comingSoon')}
                  </p>
                  <Button variant="default" className="bg-[#00E5CC] hover:bg-[#00C9B3] text-[#0d1f2d] font-medium" onClick={() => navigate('/feedback')}>
                    {t('suggestPhrase')}
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default HomePage;
